import { OrderSupport } from './OrderSupport';
import { useSearchParams } from 'react-router-dom';
import { MemberCheckout } from './MemberCheckout';
import { useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useData, post, orgID, useSession, ApiError } from '@yingbaba/api';
import { Button, Empty, Modal, PageTitle, ErrorNotice, Notice } from '@yingbaba/ui';
import { datetime, money } from '@yingbaba/utils';
import {
  memberBenefitText,
  memberStatus,
  memberCommandKey,
  clearMemberCommand,
} from '@yingbaba/utils';
import type {
  MemberPaymentMethod,
  PaidMemberOrderView,
  PaidMemberOwnerType,
  PaidMemberPlanView,
  PaidMemberPrice,
  PaidMemberOrderPage,
  PaidMemberCurrentView,
} from '@yingbaba/types';

export function MemberCenter() {
  const session = useSession();
  const [ownerType, setOwnerType] = useState<PaidMemberOwnerType>('account');
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const returnPayment = params.get('member_payment') || '';
  const returnOrder = params.get('member_order') || '';
  const paymentID = /^YBM[0-9A-HJKMNP-TV-Z]{26}$/.test(returnPayment) ? returnPayment.slice(3) : '';
  const orderID = /^[0-9A-HJKMNP-TV-Z]{26}$/.test(returnOrder) ? returnOrder : '';
  const ownerID = ownerType === 'account' ? session.data?.account.id : orgID();
  const organization = session.data?.organizations.find((o) => o.id === orgID());
  const ownerName = ownerType === 'account' ? session.data?.account.name : organization?.name;
  // Remount on scope changes so no previous account/org cursor or message leaks.
  return (
    <>
      <MemberScope
        key={`${session.data?.account.id}:${ownerType}:${ownerID}`}
        ownerType={ownerType}
        ownerID={ownerID || ''}
        ownerName={ownerName || ''}
        buyerID={session.data?.account.id || ''}
        setOwnerType={setOwnerType}
      />
      {(paymentID || orderID) && (
        <MemberCheckout
          paymentID={paymentID || undefined}
          orderID={paymentID ? undefined : orderID}
          onChanged={() => {
            void qc.invalidateQueries();
          }}
          onClose={() => {
            setParams(
              (previous) => {
                const next = new URLSearchParams(previous);
                next.delete('member_payment');
                next.delete('member_order');
                return next;
              },
              { replace: true },
            );
          }}
        />
      )}
    </>
  );
}

function MemberScope({
  ownerType,
  ownerID,
  ownerName,
  buyerID,
  setOwnerType,
}: {
  ownerType: PaidMemberOwnerType;
  ownerID: string;
  ownerName: string;
  buyerID: string;
  setOwnerType: (value: PaidMemberOwnerType) => void;
}) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [checkoutOrderID, setCheckoutOrderID] = useState('');
  const paymentMethods = useData<MemberPaymentMethod[]>('/member/payment-methods');
  const pending = useRef(false);
  const [error, setError] = useState<unknown>(null);
  const [notice, setNotice] = useState('');
  const [cursors, setCursors] = useState<string[]>(['']);
  const [purchase, setPurchase] = useState<{
    plan: PaidMemberPlanView;
    price: PaidMemberPrice;
  } | null>(null);
  const key = new URLSearchParams({ owner_type: ownerType, owner_id: ownerID }).toString();
  const plans = useData<PaidMemberPlanView[]>('/member/plans');
  const current = useData<PaidMemberCurrentView>(`/member/current?${key}`, !!ownerID, 30_000);
  const before = cursors[cursors.length - 1];
  const orders = useData<PaidMemberOrderPage>(
    `/member/orders?${key}${before ? `&before=${before}` : ''}`,
    !!ownerID && current.data?.can_view_orders === true,
    30_000,
  );
  const canPurchase = current.isSuccess && current.data.can_purchase && !busy;

  async function refresh() {
    await qc.invalidateQueries({
      predicate: (q) => typeof q.queryKey[0] === 'string' && q.queryKey[0].startsWith('/member/'),
    });
  }
  async function submitPurchase() {
    if (!purchase || !current.data?.can_purchase || pending.current) return;
    pending.current = true;
    setBusy(true);
    setError(null);
    setNotice('');
    const payload = {
      owner_type: ownerType,
      owner_id: ownerID,
      price_id: purchase.price.id,
      expected_plan_revision: purchase.plan.revision,
    };
    const scope = `order:${buyerID}:${ownerType}:${ownerID}`;
    try {
      const idempotency_key = await memberCommandKey(scope, payload);
      const created = await post<PaidMemberOrderView>('/member/orders', {
        ...payload,
        idempotency_key,
      });
      if (paymentMethods.data?.some((m) => m.available)) setCheckoutOrderID(created.id);
      clearMemberCommand(scope);
      setPurchase(null);
      setCursors(['']);
      setNotice(
        paymentMethods.data?.some((m) => m.available)
          ? '订单已创建，请选择支付方式。支付结果确认后将开通会员服务。'
          : '订单已创建。请在有效期内联系平台确认付款指引，核款后开通；请勿重复转账。',
      );
      await refresh();
    } catch (e) {
      setError(e);
      if (
        e instanceof ApiError &&
        ['MEMBER_PLAN_CHANGED', 'MEMBER_PRICE_UNAVAILABLE'].includes(e.code)
      ) {
        setPurchase(null);
        await plans.refetch();
      }
    } finally {
      pending.current = false;
      setBusy(false);
    }
  }
  async function cancel(id: string) {
    if (pending.current) return;
    pending.current = true;
    setBusy(true);
    setError(null);
    try {
      await post(`/member/orders/${id}/cancel`);
      setNotice('取消请求已提交。线上支付需等待渠道确认关单，此操作不代表退款。');
      await refresh();
    } catch (e) {
      setError(e);
    } finally {
      pending.current = false;
      setBusy(false);
    }
  }
  const available = (plans.data || []).filter((p) => p.audience === ownerType);
  const hasPending = orders.data?.items.some((o) => o.status === 'pending');
  return (
    <div>
      <PageTitle
        title="付费会员"
        description="个人与组织订阅独立，可查看服务内容、有效期和购买记录。不自动续费。"
      />
      <Notice>
        当前尚未开放可使用的会员业务权益，请以套餐中明确提供的服务为准。创建订单不会扣款，付款确认后才会开通。
      </Notice>
      <div className="row" style={{ gap: 12, margin: '20px 0' }}>
        <Button
          variant="secondary"
          disabled={busy || ownerType === 'account'}
          onClick={() => setOwnerType('account')}
        >
          个人会员
        </Button>
        <Button
          variant="secondary"
          disabled={busy || ownerType === 'organization'}
          onClick={() => setOwnerType('organization')}
        >
          当前组织会员
        </Button>
      </div>
      <ErrorNotice error={error} />
      {notice && <Notice>{notice}</Notice>}
      {!ownerID ? (
        <Empty title="暂无可用会员归属" />
      ) : (
        <>
          <section className="card" style={{ padding: 20, marginBottom: 20 }}>
            <h2>当前会员 · {ownerName}</h2>
            <ErrorNotice error={current.error} />
            {current.isLoading ? (
              <p>正在查询会员…</p>
            ) : (
              current.isSuccess && (
                <>
                  <p>
                    {memberStatus[current.data.status] || current.data.status}
                    {current.data.subscription && (
                      <>
                        {' '}
                        · {current.data.subscription.plan_name} · 到期时间：
                        {datetime(current.data.subscription.expires_at)}
                      </>
                    )}
                  </p>
                  {current.data.benefits.map((b) => (
                    <p key={b.code} className="muted">
                      {memberBenefitText(b)}
                    </p>
                  ))}
                  {current.data.upcoming_periods.length > 0 && (
                    <details>
                      <summary>已排期的续费权益</summary>
                      {current.data.upcoming_periods.map((p) => (
                        <div key={p.id}>
                          <p>
                            {p.plan_name} · {datetime(p.starts_at)} 至 {datetime(p.expires_at)}
                          </p>
                          {p.benefits.map((b) => (
                            <p key={b.code}>{memberBenefitText(b)}</p>
                          ))}
                        </div>
                      ))}
                    </details>
                  )}
                  {!current.data.can_purchase && (
                    <p className="muted">
                      当前身份仅可查看。组织购买需 owner/admin，且账号及组织处于启用状态。
                    </p>
                  )}
                </>
              )
            )}
          </section>
          <h2>会员套餐</h2>
          <ErrorNotice error={plans.error} />
          {plans.isLoading ? (
            <p>正在加载套餐…</p>
          ) : (
            plans.isSuccess &&
            (available.length === 0 ? (
              <Empty title="暂无上架的会员套餐" />
            ) : (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit,minmax(min(100%,260px),1fr))',
                  gap: 16,
                }}
              >
                {available.map((p) => (
                  <section key={p.id} className="card" style={{ padding: 20 }}>
                    <h3>{p.name}</h3>
                    <p>{p.description}</p>
                    {p.benefits.map((b) => (
                      <p className="muted" key={b.code}>
                        {memberBenefitText(b)}
                      </p>
                    ))}
                    {!p.benefits.length && <p className="muted">尚未配置业务权益。</p>}
                    {p.prices
                      .filter((x) => x.status === 'active')
                      .map((price) => {
                        const different =
                          current.data?.subscription?.status === 'active' &&
                          current.data.subscription.plan_id !== p.id;
                        return (
                          <div key={price.id} style={{ marginTop: 16 }}>
                            <p>
                              {price.duration_months} 个月 · {money(price.amount_minor)}
                            </p>
                            <Button
                              disabled={!canPurchase || !!hasPending || different}
                              onClick={() => {
                                setError(null);
                                setPurchase({ plan: p, price });
                              }}
                            >
                              {different
                                ? '暂不支持跨等级续费'
                                : hasPending
                                  ? '请先处理待核款申请'
                                  : '申请购买'}
                            </Button>
                          </div>
                        );
                      })}
                    {!p.prices.some((x) => x.status === 'active') && (
                      <p className="muted">暂无可购买价格。</p>
                    )}
                  </section>
                ))}
              </div>
            ))
          )}
          {current.data?.can_view_orders && (
            <section style={{ marginTop: 28 }}>
              <h2>购买记录</h2>
              <ErrorNotice error={orders.error} />
              {orders.isLoading ? (
                <p>正在加载购买记录…</p>
              ) : (
                orders.isSuccess &&
                (orders.data.items.length ? (
                  orders.data.items.map((o) => (
                    <div className="list-item" key={o.id}>
                      <div className="grow">
                        <strong>
                          {o.plan_name} · {money(o.amount_minor)}
                        </strong>
                        <p>
                          {o.duration_months} 个月 · {memberStatus[o.status] || o.status}
                        </p>
                        <p className="small muted">订单号：{o.id}</p>
                        <p className="small muted">申请有效至：{datetime(o.expires_at)}</p>
                        <details>
                          <summary>查看购买时权益快照</summary>
                          {o.benefits.length ? (
                            o.benefits.map((b) => <p key={b.code}>{memberBenefitText(b)}</p>)
                          ) : (
                            <p>未配置业务权益。</p>
                          )}
                        </details>
                      </div>
                      <OrderSupport kind="member" orderID={o.id} disabled={busy} />
                      {(o.collection_mode === 'online' ||
                        (o.status === 'pending' &&
                          paymentMethods.data?.some((m) => m.available))) && (
                        <Button variant="secondary" onClick={() => setCheckoutOrderID(o.id)}>
                          打开收银台
                        </Button>
                      )}
                      {current.data.can_manage && ['pending', 'expired'].includes(o.status) && (
                        <Button variant="secondary" disabled={busy} onClick={() => cancel(o.id)}>
                          取消申请
                        </Button>
                      )}
                    </div>
                  ))
                ) : (
                  <Empty title="暂无会员购买记录" />
                ))
              )}
              <div className="row" style={{ gap: 12, marginTop: 16 }}>
                <Button
                  variant="secondary"
                  disabled={busy || cursors.length < 2}
                  onClick={() => setCursors((c) => c.slice(0, -1))}
                >
                  上一页购买记录
                </Button>
                <Button
                  variant="secondary"
                  disabled={busy || !orders.data?.next_before_id}
                  onClick={() => setCursors((c) => [...c, orders.data!.next_before_id!])}
                >
                  下一页购买记录
                </Button>
              </div>
            </section>
          )}
        </>
      )}
      {checkoutOrderID && (
        <MemberCheckout
          orderID={checkoutOrderID}
          onClose={() => setCheckoutOrderID('')}
          onChanged={() => {
            void refresh();
          }}
        />
      )}
      {purchase && (
        <Modal
          open
          title="确认会员购买申请"
          onOpenChange={(open) => {
            if (!open && !busy) setPurchase(null);
          }}
        >
          <p>
            归属：{ownerType === 'account' ? '个人' : '组织'} · {ownerName}
          </p>
          <p>
            {purchase.plan.name} · {purchase.price.duration_months} 个月 ·{' '}
            {money(purchase.price.amount_minor)}
          </p>
          {purchase.plan.benefits.map((b) => (
            <p key={b.code}>{memberBenefitText(b)}</p>
          ))}
          <Notice>
            仅创建申请，不会扣款。有效会员同等级续费从原到期日接续，新权益在对应服务周期开始时生效。此处配置不表示业务权益已接入。
          </Notice>
          <ErrorNotice error={error} />
          <Button disabled={busy} onClick={submitPurchase}>
            {busy ? '正在提交…' : '确认申请'}
          </Button>
        </Modal>
      )}
    </div>
  );
}
